import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Announcement,
  Button,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  Layout,
  LoaderSkeleton,
  MinusPlusButtons,
  ModalV2 as Modal,
  ProgressBar,
  TextArea,
} from '@utilities';

import styles from './_index.module.scss';
import { createQuestionnaireJob, readQuestionnaireJob } from '@api/instant_survey';
import { SurveyContext } from '../../../../utilities/context';

import {
  DEFAULT_BASE_LENGTH,
  LONG_POLLING_THRESHOLD_MS,
  MAX_QUESTIONS_PER_RUN,
  PER_STEP_DURATIONS_MS,
  PROGRESS_STEPS,
} from './utilities/constants';

import { clamp, clearTimer } from './utilities/helpers';

const ModalAIGeneratedQuestionnaire = ({
  initialSurveyLength,
  maxQuestionsAvailable,
  onAddAIQuestions,
  onClose,
  onGenerationError,
}) => {
  const { activeQuestions, setJobId, setShowAIGenerationModal, setSurveyGoals, surveyGoals } =
    useContext(SurveyContext);

  const normalizedMaxQuestionsAvailable =
    typeof maxQuestionsAvailable === 'number' ? maxQuestionsAvailable : MAX_QUESTIONS_PER_RUN;
  const remainingSlots = Math.max(normalizedMaxQuestionsAvailable, 0);
  const maxSelectableLength = Math.min(remainingSlots, MAX_QUESTIONS_PER_RUN);
  const hasSlots = remainingSlots > 0;

  const [goals, setGoals] = useState(surveyGoals || '');
  const [activeJobId, setActiveJobId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [questionnaireLength, setQuestionnaireLength] = useState(() => {
    const base =
      typeof initialSurveyLength === 'number' ? initialSurveyLength : DEFAULT_BASE_LENGTH;
    return clamp(base, 0, maxSelectableLength);
  });
  const [isLongPolling, setIsLongPolling] = useState(false);

  const surveyGptJobs = useSelector((state) => state?.websocket?.surveygptJobs);
  const longPollTimerRef = useRef(null);
  const stepTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pendingGoalsRef = useRef('');

  const clearProgressTimers = () => {
    clearTimer(stepTimerRef);
    clearTimer(longPollTimerRef);
  };

  useEffect(() => {
    setQuestionnaireLength((prev) => {
      const base =
        typeof initialSurveyLength === 'number' ? initialSurveyLength : prev || DEFAULT_BASE_LENGTH;

      return maxSelectableLength === 0 ? 0 : clamp(base, 0, maxSelectableLength);
    });
  }, [initialSurveyLength, maxSelectableLength]);

  useEffect(() => {
    clearProgressTimers();

    if (!isPolling) {
      setProgressStep(0);
      setIsLongPolling(false);
      return;
    }

    setProgressStep(0);
    setIsLongPolling(false);
    longPollTimerRef.current = setTimeout(() => {
      setIsLongPolling(true);
    }, LONG_POLLING_THRESHOLD_MS);
    const lastIndex = PROGRESS_STEPS.length - 1;

    const scheduleNext = (index) => {
      if (!isPolling || index >= lastIndex) return;

      const duration = PER_STEP_DURATIONS_MS[index] || 2500;
      stepTimerRef.current = setTimeout(() => {
        setProgressStep((prev) => {
          const next = Math.min(prev + 1, lastIndex);
          if (next < lastIndex) scheduleNext(next);
          return next;
        });
      }, duration);
    };

    scheduleNext(0);

    return clearProgressTimers;
  }, [isPolling]);

  useEffect(() => {
    return () => {
      clearProgressTimers();
      abortControllerRef.current?.abort?.();
    };
  }, []);

  useEffect(() => {
    if (!activeJobId) return;

    const jobStatus = surveyGptJobs?.find(({ id }) => Number(id) === Number(activeJobId));

    if (!jobStatus?.status) return;

    const pendingGoals = pendingGoalsRef.current;

    if (jobStatus.status === 'COMPLETED') {
      const jobId = activeJobId;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setActiveJobId(null);

      const fetchCompletedJob = async () => {
        try {
          const response = await readQuestionnaireJob({
            jobId,
            signal: controller.signal,
          });
          const questions = response?.output?.questions;

          if (Array.isArray(questions) && questions.length > 0) {
            onAddAIQuestions(questions);
            setShowAIGenerationModal(false);
            setSurveyGoals(pendingGoals);
          } else {
            onGenerationError(
              true,
              'AI could not generate any questions. Try adjusting your goals.'
            );
            setShowAIGenerationModal(false);
            setSurveyGoals(pendingGoals);
          }
        } catch (error) {
          if (controller.signal.aborted || error?.code === 'ERR_CANCELED') return;

          onGenerationError(
            true,
            'We encountered an issue generating questions. Please try again.'
          );
          setShowAIGenerationModal(false);
          setSurveyGoals(pendingGoals);
          console.error(error);
        } finally {
          setIsPolling(false);
          abortControllerRef.current = null;
        }
      };

      fetchCompletedJob();
      return;
    }

    if (jobStatus.status === 'FAILED') {
      setActiveJobId(null);
      onGenerationError(
        true,
        jobStatus.message || 'We encountered an issue generating questions. Please try again.'
      );
      setShowAIGenerationModal(false);
      setSurveyGoals(pendingGoals);
      setIsPolling(false);
      abortControllerRef.current = null;
    }
  }, [
    activeJobId,
    onAddAIQuestions,
    onGenerationError,
    setShowAIGenerationModal,
    setSurveyGoals,
    surveyGptJobs,
  ]);

  const handleAIQuestionnaireGenerate = async () => {
    if (!hasSlots || !goals.trim()) return;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      setIsPolling(true);
      onGenerationError(false);
      const desiredLength = clamp(questionnaireLength, 0, maxSelectableLength);
      const job = await createQuestionnaireJob({
        length: desiredLength,
        signal: controller.signal,
        userInput: goals,
      });

      const surveyGptJobId = job?.id;

      if (!surveyGptJobId) {
        throw new Error('Missing surveygpt_job_id');
      }

      pendingGoalsRef.current = goals;
      setJobId(surveyGptJobId);
      setActiveJobId(surveyGptJobId);
      abortControllerRef.current = null;
    } catch (error) {
      if (
        controller.signal.aborted ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError'
      ) {
        return;
      }

      onGenerationError(true, 'We encountered an issue generating questions. Please try again.');
      setShowAIGenerationModal(false);
      setSurveyGoals(goals);
      console.error(error);
      setIsPolling(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort?.();
    onClose();
  };

  const maxCopySlots = Math.min(remainingSlots, MAX_QUESTIONS_PER_RUN);
  const slotCopy = hasSlots
    ? `You can add up to ${maxCopySlots} more question${maxCopySlots === 1 ? '' : 's'}.`
    : 'You have reached the maximum number of questions.';

  const lengthControlDisabled = !hasSlots;

  const actionButtons = [
    <Button key="modal-cancel" onClick={handleCancel} text="Cancel" variant="secondary" />,
    <Button
      isDisabled={!goals.trim() || !hasSlots}
      isLoading={isPolling}
      key="modal-generate"
      onClick={handleAIQuestionnaireGenerate}
      text="Generate Questionnaire"
    />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      id="modal-ai-generated-questionnaire"
      isActive
      onClose={handleCancel}
      title="AI Generated Questionnaire"
    >
      <div className={styles['modal-ai-content']}>
        {isPolling ? (
          <div className={styles['modal-ai-loading']}>
            <div aria-live="polite" className={styles['modal-ai-progress-card']}>
              {PROGRESS_STEPS.map((step, index) => {
                const isComplete = index < progressStep;
                const isActive = index === progressStep;
                const value = isComplete ? 100 : isActive ? 40 : 0;

                return (
                  <div className={styles['modal-ai-progress-row']} key={step}>
                    <span
                      className={classNames(styles['modal-ai-progress-label'], {
                        [styles['is-active']]: isActive,
                        [styles['is-complete']]: isComplete,
                        [styles['is-pending']]: !isActive && !isComplete,
                      })}
                    >
                      {step}
                    </span>
                    <div className={styles['modal-ai-progress-bar']}>
                      <ProgressBar aria-label={`${step} progress`} max={100} value={value} />
                    </div>
                    <div className={styles['modal-ai-progress-status']}>
                      {isComplete && <CheckCircleIcon />}
                    </div>
                  </div>
                );
              })}
            </div>

            <LoaderSkeleton height={120}>
              <rect height="8" rx="4" ry="4" width="100%" x="0" y="10" />
              <rect height="8" rx="4" ry="4" width="100%" x="0" y="40" />
              <rect height="8" rx="4" ry="4" width="100%" x="0" y="70" />
            </LoaderSkeleton>

            {isLongPolling && (
              <p className={styles['modal-ai-long-polling-note']}>
                We&apos;re still generating your questionnaire. Thanks for your patience.
              </p>
            )}
          </div>
        ) : (
          <>
            {activeQuestions?.length > 0 && (
              <Announcement
                icon={<ExclamationTriangleIcon />}
                text="New questions will be added to your questionnaire. You can insert up to 20 questions in total."
                variant="warn"
              />
            )}
            <div className={styles['modal-ai-input-group']}>
              <TextArea
                label="GOALS AND OBJECTIVES"
                onChange={setGoals}
                placeholder="What are you looking to learn from this survey?"
                value={goals}
              />
            </div>
            <p className={styles['modal-ai-slot-message']}>{slotCopy}</p>
            <Layout.Flex className={styles['modal-ai-input-group']} gap="x-small">
              <span className={styles['modal-ai-questionnaire-length-label']}>
                Questionnaire Length (max {maxSelectableLength})
              </span>
              <strong>{questionnaireLength}</strong>
              <MinusPlusButtons
                disableMinusButton={questionnaireLength <= 0 || lengthControlDisabled}
                disablePlusButton={
                  lengthControlDisabled || questionnaireLength >= maxSelectableLength
                }
                onClickMinus={() =>
                  setQuestionnaireLength((prev) => clamp(prev - 1, 0, maxSelectableLength))
                }
                onClickPlus={() =>
                  setQuestionnaireLength((prev) => clamp(prev + 1, 0, maxSelectableLength))
                }
              />
            </Layout.Flex>
            <p className={styles['modal-ai-note']}>
              By proceeding, you consent to the information above being shared with OpenAI to
              generate your survey questions. No account information is shared. See the{' '}
              <a href="https://openai.com/policies/terms-of-use/" rel="noreferrer" target="_blank">
                OpenAI Terms
              </a>{' '}
              for details.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

ModalAIGeneratedQuestionnaire.propTypes = {
  initialSurveyLength: PropTypes.number,
  maxQuestionsAvailable: PropTypes.number.isRequired,
  onAddAIQuestions: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerationError: PropTypes.func.isRequired,
};

export default ModalAIGeneratedQuestionnaire;
